import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaCamera, FaClipboardList, FaEllipsisH } from 'react-icons/fa';

/**
 * Acciones para pedir foto o conteo interno (bodega) sobre un producto.
 * Aparece en buscador, ficha y preguntas.
 */
const SolicitudInternaButton = ({
    token,
    idProducto,
    esPadre = false,
    nombreProducto = '',
    variant = 'icons', // 'icons' | 'menu' | 'buttons'
    /** En footers oscuros usa 'light'; en tablas/fondos claros usa 'dark'. */
    tone = 'light',
    className = '',
}) => {
    const [busy, setBusy] = useState(false);

    if (!token || !idProducto) return null;

    const iconBtnClass =
        tone === 'dark'
            ? 'text-indigo-600 hover:text-indigo-800 disabled:opacity-40'
            : 'text-white hover:opacity-80 disabled:opacity-40';
    const conteoBtnClass =
        tone === 'dark'
            ? 'text-slate-700 hover:text-slate-900 disabled:opacity-40'
            : 'text-white hover:opacity-80 disabled:opacity-40';

    const solicitar = async (tipo) => {
        if (tipo === 'conteo' && esPadre) {
            Swal.fire(
                'Producto padre',
                'El conteo se pide sobre una variante (hijo), no sobre el producto padre.',
                'info'
            );
            return;
        }

        const label = tipo === 'foto' ? 'toma de foto' : 'conteo en bodega';
        const { value: nota, isConfirmed } = await Swal.fire({
            title: `Solicitar ${label}`,
            html: nombreProducto
                ? `<p class="text-sm text-slate-600 mb-2">${nombreProducto}</p>`
                : undefined,
            input: 'textarea',
            inputLabel: 'Nota para bodega (opcional)',
            inputPlaceholder: 'Ej. faltan ángulos laterales / revisar estantería A3…',
            inputAttributes: { maxlength: 500 },
            showCancelButton: true,
            confirmButtonText: 'Solicitar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#4f46e5',
        });

        if (!isConfirmed) return;

        setBusy(true);
        try {
            await axios.post(
                '/api/ecommerce/solicitudes-internas',
                {
                    tipo,
                    id_producto: idProducto,
                    nota: typeof nota === 'string' ? nota.trim() || undefined : undefined,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            Swal.fire({
                icon: 'success',
                title: 'Solicitud enviada',
                text:
                    tipo === 'foto'
                        ? 'Quedó en Solicitudes de foto para bodega.'
                        : 'Quedó en Validación bodega para conteo.',
                timer: 2200,
                showConfirmButton: false,
            });
        } catch (error) {
            const status = error.response?.status;
            const msg = error.response?.data?.message;
            if (status === 409) {
                Swal.fire('Ya existe', msg || 'Ya hay una solicitud pendiente.', 'info');
            } else {
                Swal.fire('Error', msg || 'No se pudo crear la solicitud.', 'error');
            }
        } finally {
            setBusy(false);
        }
    };

    const stop = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
    };

    if (variant === 'buttons') {
        return (
            <div className={`flex flex-wrap gap-2 ${className}`} onClick={stop}>
                <button
                    type="button"
                    disabled={busy}
                    onClick={(e) => { stop(e); solicitar('foto'); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                    title="Pedir foto a bodega"
                >
                    <FaCamera /> Pedir foto
                </button>
                {!esPadre && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={(e) => { stop(e); solicitar('conteo'); }}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
                        title="Pedir conteo a bodega"
                    >
                        <FaClipboardList /> Pedir conteo
                    </button>
                )}
            </div>
        );
    }

    if (variant === 'menu') {
        return (
            <div className={`relative inline-flex ${className}`} onClick={stop}>
                <details className="group">
                    <summary
                        className="list-none cursor-pointer text-white hover:opacity-80 p-1 rounded"
                        title="Solicitudes internas"
                    >
                        <FaEllipsisH className="w-4 h-4" />
                    </summary>
                    <div className="absolute right-0 z-30 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-sm text-gray-800">
                        <button
                            type="button"
                            disabled={busy}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                            onClick={(e) => { stop(e); solicitar('foto'); }}
                        >
                            <FaCamera className="text-indigo-600" /> Pedir foto
                        </button>
                        {!esPadre && (
                            <button
                                type="button"
                                disabled={busy}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                                onClick={(e) => { stop(e); solicitar('conteo'); }}
                            >
                                <FaClipboardList className="text-slate-700" /> Pedir conteo
                            </button>
                        )}
                    </div>
                </details>
            </div>
        );
    }

    // variant === 'icons' (default): compact for ProductCard footer / table
    return (
        <div className={`inline-flex items-center gap-2 ${className}`} onClick={stop}>
            <button
                type="button"
                disabled={busy}
                onClick={(e) => { stop(e); solicitar('foto'); }}
                className={iconBtnClass}
                title="Pedir foto a bodega"
            >
                <FaCamera className="w-5 h-5" />
            </button>
            {!esPadre && (
                <button
                    type="button"
                    disabled={busy}
                    onClick={(e) => { stop(e); solicitar('conteo'); }}
                    className={conteoBtnClass}
                    title="Pedir conteo a bodega"
                >
                    <FaClipboardList className="w-5 h-5" />
                </button>
            )}
        </div>
    );
};

export default SolicitudInternaButton;
